/**
 * Client-side checks for the single file a reporter can attach to an issue report.
 *
 * The service checks the same rules before it issues an upload URL and again when the report is filed,
 * so these mirror them to show inline errors before any request is sent. They return i18n keys so the
 * modal can translate them.
 */

export const MAX_ATTACHMENT_BYTES = 80 * 1024 * 1024;

const ALLOWED_ATTACHMENT_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.bmp',
  '.heic',
  '.mp4',
  '.mov',
  '.webm',
  '.csv',
  '.pdf',
  '.xlsx',
  '.xls',
] as const;

/** Steers the file picker only; `validateAttachmentFile` still checks every file. */
export const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_EXTENSIONS.join(',');

const UNTYPED_CONTENT_TYPE = 'application/octet-stream';
// Browsers leave `File.type` empty for types they cannot name, such as HEIC on most platforms.
const UNTYPED_CONTENT_TYPES: ReadonlySet<string> = new Set(['', UNTYPED_CONTENT_TYPE]);
const ALLOWED_CONTENT_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/heic',
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

const MAX_FILENAME_LENGTH = 255;
const RESERVED_FILENAME_CHARACTERS = /[<>:"/\\|?*]/;
const FIRST_PRINTABLE_CODE_POINT = 32;
const DELETE_CODE_POINT = 127;
const EXTENSION_SEPARATOR = '.';

export function validateAttachmentFile (file: File): string[] {
  return [...validateFileSize(file.size), ...validateFileType(file), ...validateFilename(file.name)];
}

/** The type the file is announced and uploaded with; a file the browser cannot name is sent as untyped bytes. */
export function getAttachmentContentType (file: File): string {
  return file.type || UNTYPED_CONTENT_TYPE;
}

function validateFileSize (sizeBytes: number): string[] {
  return sizeBytes < 1 || sizeBytes > MAX_ATTACHMENT_BYTES
    ? ['issue_report_file_error_size']
    : [];
}

function validateFileType (file: File): string[] {
  return isAllowedExtension(getExtension(file.name)) && isAllowedContentType(file.type)
    ? []
    : ['issue_report_file_error_type'];
}

function getExtension (filename: string): string {
  const separatorIndex = filename.lastIndexOf(EXTENSION_SEPARATOR);
  return separatorIndex === -1 ? '' : filename.slice(separatorIndex).toLowerCase();
}

function isAllowedExtension (extension: string): boolean {
  return ALLOWED_ATTACHMENT_EXTENSIONS.some(allowed => allowed === extension);
}

function isAllowedContentType (contentType: string): boolean {
  return UNTYPED_CONTENT_TYPES.has(contentType) || ALLOWED_CONTENT_TYPES.has(contentType);
}

function validateFilename (filename: string): string[] {
  const characters = Array.from(filename);
  const errors: string[] = [];

  if (characters.length < 1 || characters.length > MAX_FILENAME_LENGTH) {
    errors.push('issue_report_file_error_name_length');
  }
  if (filename.endsWith('.') || filename.endsWith(' ')) {
    errors.push('issue_report_file_error_name_ending');
  }
  if (RESERVED_FILENAME_CHARACTERS.test(filename)) {
    errors.push('issue_report_file_error_name_reserved');
  }
  if (characters.some(isControlCharacter)) {
    errors.push('issue_report_file_error_name_control');
  }
  return errors;
}

function isControlCharacter (character: string): boolean {
  const codePoint = character.codePointAt(0)!;
  return codePoint < FIRST_PRINTABLE_CODE_POINT || codePoint === DELETE_CODE_POINT;
}
